import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * The SINGLE status → {label, colourClass} mapping for the customer Pricing
 * module (Story 1.5, Design Spec §5). Every customer screen (Epics 3-4, 1.6)
 * resolves a badge from this one table — do not fork it.
 *
 * Keys are the raw DTO `status` enum values emitted by Story 1.3, plus the
 * directory rows' `active` / `cancelled`. Colour classes live in
 * `pricing-status.css` and are token-only (§5 semantics):
 *   pending = amber, update = accent, accepted/active = green, cancelled = red.
 *
 * NOTE (Q-1.5-A): the separate Supplier Portal app re-implements this tiny
 * map from §5. For MMP, duplicating it across the two apps is the simplest
 * path (assumed acceptable) rather than a shared package.
 */
export const PRICING_STATUS_MAP: Record<string, { label: string; colourClass: string }> = {
  pending_review: { label: 'Pending Review', colourClass: 'pf-status--pending' },
  update_requested_awaiting_customer: { label: 'Update Submitted', colourClass: 'pf-status--update' },
  accepted: { label: 'Accepted', colourClass: 'pf-status--accepted' },
  active: { label: 'Active', colourClass: 'pf-status--active' },
  cancelled: { label: 'Cancelled', colourClass: 'pf-status--cancelled' },
};

/** Fail-safe class for an unknown status value (renders neutral, never crashes). */
const NEUTRAL_CLASS = 'pf-status--neutral';

/**
 * Status badge (Story 1.5, Design Spec §5).
 *
 * Maps a negotiation `status` to badge text + a colour treatment from the
 * single PRICING_STATUS_MAP. The text is always present (never colour-only)
 * and the pill carries `role="status"` + `aria-label="Status: <label>"`.
 *
 * An optional `label` override lets a context-specific screen relabel without
 * forking the mapping (e.g. the customer "Awaiting Supplier" wording maps to
 * the same underlying `pending_review` status).
 *
 * Pure @Input() → render. No events, no CrudService/HTTP, no route.
 */
@Component({
  selector: 'app-status-badge',
  templateUrl: './status-badge.component.html',
  styleUrls: ['../pricing-status.css', './status-badge.component.css'],
  imports: [CommonModule],
  standalone: true,
})
export class StatusBadgeComponent {
  /** Raw DTO status value, e.g. `pending_review`, `accepted`, `active`. */
  @Input() status = '';
  /** Optional context-specific label override (e.g. "Awaiting Supplier"). */
  @Input() label?: string;

  /** Resolve the §5 mapping; unknown status ⇒ neutral, raw value as label. */
  private get entry(): { label: string; colourClass: string } {
    return PRICING_STATUS_MAP[this.status] ?? { label: this.status, colourClass: NEUTRAL_CLASS };
  }

  /**
   * A non-empty label override wins; otherwise the mapped label. An empty
   * string is treated as "no override" (falls back to the map) so a stray
   * `label=""` never blanks the badge.
   */
  get displayLabel(): string {
    return this.label && this.label.length > 0 ? this.label : this.entry.label;
  }

  get colourClass(): string {
    return this.entry.colourClass;
  }

  get ariaLabel(): string {
    return `Status: ${this.displayLabel}`;
  }
}
